// frontend/src/components/AboutSummary.js
import { useState } from "react";
import { fetchAboutSummary } from "../lib/api";

export default function AboutSummary() {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function toggle() {
    const next = !open;
    setOpen(next);

    if (next && !summary && !loading) {
      setLoading(true);
      setError("");
      try {
        const text = await fetchAboutSummary();
        setSummary(text);
      } catch (err) {
        setError(err?.message || "Could not generate summary.");
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-900 px-3 py-1.5 text-[11px] font-medium text-amber-100 shadow-md shadow-slate-900/20 transition hover:-translate-y-0.5 hover:bg-slate-800 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
        title="Explain this platform in plain English"
      >
        <span className="text-sm leading-none">✨</span>
        <span>AI summary</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-20 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 p-4 shadow-2xl shadow-slate-900/40 cc-fade-up sm:w-96"
          role="region"
          aria-label="AI summary of this platform"
        >
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400" />
              <span
                className="h-1.5 w-1.5 animate-pulse rounded-full bg-orange-400"
                style={{ animationDelay: "120ms" }}
              />
              <span
                className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-400"
                style={{ animationDelay: "240ms" }}
              />
              <span className="ml-1">Writing a plain-English summary…</span>
            </div>
          )}

          {error && !loading && (
            <p className="text-xs leading-relaxed text-rose-300" role="alert">
              {error}
            </p>
          )}

          {summary && !loading && (
            <>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-400">
                What this is about
              </p>
              <div className="mt-2 space-y-2.5">
                {summary
                  .split(/\n{2,}/)
                  .filter(Boolean)
                  .map((para, i) => (
                    <p key={i} className="text-sm leading-relaxed text-slate-200">
                      {para}
                    </p>
                  ))}
              </div>
              <p className="mt-3 border-t border-slate-800 pt-2 text-[10px] leading-relaxed text-slate-500">
                Plain-English explanation. Not legal or security advice.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}